import { SERIES_COLORS, SERIES_FADED_COLORS, type HorizontalBarChartPayload } from "../normalize.js";
import { valueAxisTicks } from "./geometry/axisTicks.js";
import { horizontalBarPlotWidth } from "./geometry/plotWidth.js";
import { mountD3HorizontalBarChart } from "./d3/horizontalBar.js";

/** Value axis never compresses below this, even when every value is small. */
const HORIZONTAL_BAR_FLOOR_MAX = 45;
const HORIZONTAL_BAR_SPACING_LEFT = 20;
const HORIZONTAL_BAR_SPACING_RIGHT = 32;

/**
 * Declared bounds + ticks for the value axis, computed from a *predicted* plot width
 * so no post-layout patch is needed — see ./geometry/plotWidth.ts for why patching
 * after render was rejected.
 */
export function horizontalBarValueTicks(
  data: HorizontalBarChartPayload,
  chartWidth: number
): { min: number; max: number; positions: number[]; interval: number } {
  const finite = data.series.flatMap((s) => s.data.filter((n) => Number.isFinite(n)));
  const { plotWidth } = horizontalBarPlotWidth({
    chartWidth,
    categories: data.categories,
    spacingLeft: HORIZONTAL_BAR_SPACING_LEFT,
  });
  return valueAxisTicks({
    softMin: 0,
    dataMin: finite.length ? Math.min(...finite) : 0,
    dataMax: finite.length ? Math.max(...finite) : 0,
    floorMax: HORIZONTAL_BAR_FLOOR_MAX,
    plotWidth,
  });
}

export const HORIZONTAL_BAR_ROW_PX = 28;
export const HORIZONTAL_BAR_CHART_PAD_PX = 80;

export function colorIndexForSeriesName(name: string, fallbackIndex: number): number {
  if (/portfolio/i.test(name)) return 0;
  if (/benchmark/i.test(name)) return 2;
  return fallbackIndex % SERIES_COLORS.length;
}

export function seriesColors(name: string, fallbackIndex: number): { baseColor: string; fadedColor: string } {
  const colorIndex = colorIndexForSeriesName(name, fallbackIndex);
  return {
    baseColor: String(SERIES_COLORS[colorIndex]),
    fadedColor: String(SERIES_FADED_COLORS[colorIndex]),
  };
}

export function horizontalBarChartHeight(categoryCount: number): number {
  return Math.max(HORIZONTAL_BAR_ROW_PX + HORIZONTAL_BAR_CHART_PAD_PX, categoryCount * HORIZONTAL_BAR_ROW_PX + HORIZONTAL_BAR_CHART_PAD_PX);
}

export interface MountHorizontalBarChartOptions {
  esc: (s: string) => string;
}

export function mountHorizontalBarChart(
  mount: HTMLElement,
  data: HorizontalBarChartPayload,
  opts: MountHorizontalBarChartOptions
): () => void {
  const { esc } = opts;

  return mountD3HorizontalBarChart(mount, data, { esc });
}
